import type { RefObject } from 'react';
import React, { useRef } from 'react';
import classNames from 'clsx';
import Link from '../../../../../../common/Link/Link';
import { formatClassNames } from '../../../../../../utils/classNames';
import { isEmptyObject } from '../../../../../../utils/dataUtils';
import type { IMenuItemProps } from '../../../Menu.types';
import type { DropdownAnimationNameType } from '../../../constants';
import { createSDKAction } from './utils';
import classes from './style/MenuItem.module.scss';
import { MenuParts, selectors } from '../../../constants';
import { isCurrentItem } from '../../../menuItems';
import {
  menuSemanticClassNames,
  shmSemanticClassNames,
} from '../../../semanticClassNames';
import { AnimationPhase, type AnimationState } from './useAnimationState';
import { MenuItemDropdownIcon } from './MenuItemDropdownIcon';

export const MenuItemLabel: React.FC<
  IMenuItemProps & {
    hasDropdownMenu: boolean;
    hasSubItems: boolean;
    hasContainer: boolean;
    chevronButtonRef: any;
    toggleDropdown: () => void;
    isOpen: boolean;
    dropdownAnimationState: AnimationState<DropdownAnimationNameType>;
    submenuToggleId?: string;
  }
> = props => {
  const {
    item,
    currentItem,
    hasDropdownMenu,
    hasSubItems,
    hasContainer,
    onItemClick,
    onItemDblClick,
    onItemMouseIn,
    onItemMouseOut,
    partToPreviewStateMap,
    translations,
    chevronButtonRef,
    toggleDropdown,
    isOpen,
    dropdownAnimationState,
    submenuToggleId,
  } = props;

  const itemLabelClassNames = classNames(
    classes.label,
    formatClassNames(shmSemanticClassNames.menuItemLabel),
    formatClassNames(menuSemanticClassNames.itemLabel),
  );

  const isCurrentPage = isCurrentItem(item, currentItem);
  const { label, link } = item;
  const hasLink = !isEmptyObject(link);
  const linkRef: RefObject<HTMLAnchorElement> = useRef<HTMLAnchorElement>(null);

  const labelContainerClassNames = classNames(
    selectors.item,
    classes.labelContainer,
    formatClassNames(shmSemanticClassNames.menuItemWrapper),
    formatClassNames(menuSemanticClassNames.item),
  );

  const isInteractive = (hasSubItems || hasLink) && !isCurrentPage;

  const isDropdownIconOpen =
    isOpen && dropdownAnimationState.phase !== AnimationPhase.ExitActive;

  return (
    <div
      className={labelContainerClassNames}
      data-selected={isCurrentPage ? true : undefined}
      data-part={MenuParts.MenuItemContent}
      data-preview={partToPreviewStateMap?.item}
      data-interactive={isInteractive}
      onClick={createSDKAction(item, isCurrentPage, onItemClick)}
      onMouseEnter={createSDKAction(item, isCurrentPage, onItemMouseIn)}
      onMouseLeave={createSDKAction(item, isCurrentPage, onItemMouseOut)}
      onDoubleClick={createSDKAction(item, isCurrentPage, onItemDblClick)}
    >
      <Link
        ref={linkRef}
        {...link}
        dataPart={MenuParts.MenuItemLink}
        activateByKey="Enter"
        {...(isCurrentPage && {
          'aria-current': 'page',
        })}
      >
        <div className={itemLabelClassNames} data-part={MenuParts.Label}>
          {label}
        </div>
      </Link>
      {hasDropdownMenu &&
        ['default', 'srOnly'].map(variant => (
          <MenuItemDropdownIcon
            key={variant}
            ariaLabel={translations.dropdownButtonAriaLabel?.replace(
              '<%= itemName %>',
              label,
            )}
            chevronButtonRef={chevronButtonRef}
            onClick={toggleDropdown}
            isOpen={isOpen}
            hasSubItems={hasSubItems}
            hasContainer={hasContainer}
            hasLink={hasLink}
            isDropdownIconOpen={isDropdownIconOpen}
            className={variant === 'srOnly' ? classes.srOnly : undefined}
            srOnly={variant === 'srOnly'}
            submenuToggleId={variant === 'srOnly' ? undefined : submenuToggleId}
          />
        ))}
    </div>
  );
};
